import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FiCheck } from 'react-icons/fi';
import colors from '../../styles/colors';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(201, 162, 39, 0.4); }
  50% { box-shadow: 0 0 0 15px rgba(201, 162, 39, 0); }
`;

const AboutSection = styled.section`
  padding: 120px 40px;
  background: ${colors.gradient.forest};
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 50px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;

  @media (max-width: 900px) {
    order: -1;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  background: url('/images/about.jpg') center/cover no-repeat;
  border-radius: 24px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
`;

const YearsBadge = styled.div`
  position: absolute;
  bottom: -20px;
  right: 30px;
  background: ${colors.gradient.gold};
  padding: 25px 30px;
  border-radius: 20px;
  text-align: center;
  box-shadow: ${colors.shadowGold};
  animation: ${pulse} 3s ease-in-out infinite;

  @media (max-width: 500px) {
    right: 10px;
    padding: 20px 25px;
  }
`;

const YearsNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${colors.primary.dark};
  line-height: 1;
`;

const YearsText = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${colors.primary.dark};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Content = styled.div``;

const Subtitle = styled.div`
  color: ${colors.accent.gold};
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 2.8rem);
  color: ${colors.text.white};
  margin-bottom: 24px;
  font-weight: 800;
  line-height: 1.2;
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: ${colors.text.light};
  line-height: 1.9;
  margin-bottom: 35px;
`;

const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 40px;
`;

const CheckItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 1.05rem;
  color: ${colors.text.light};
  font-weight: 500;

  svg {
    flex-shrink: 0;
    color: ${colors.primary.dark};
    background: ${colors.accent.gold};
    padding: 6px;
    border-radius: 50%;
    font-size: 1.3rem;
  }
`;

const CTAButton = styled.button`
  padding: 16px 36px;
  background: ${colors.gradient.gold};
  color: ${colors.primary.dark};
  font-weight: 700;
  font-size: 1rem;
  border-radius: 50px;
  box-shadow: ${colors.shadowGold};

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(201, 162, 39, 0.5);
  }
`;

export default function About() {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <AboutSection id="about">
      <Container>
        <ImageWrapper>
          <ImagePlaceholder />
          <YearsBadge>
            <YearsNumber>19+</YearsNumber>
            <YearsText>Years</YearsText>
          </YearsBadge>
        </ImageWrapper>

        <Content>
          <Subtitle>About Us</Subtitle>
          <Title>Your Trusted Partner in Landscaping Since 2005</Title>
          <Description>
            Greenridge Landscaping has been providing quality services
            to residential and commercial clients throughout the region for nearly two decades.
            We pride ourselves on delivering exceptional results and building lasting
            relationships with our customers.
          </Description>

          <CheckList>
            <CheckItem><FiCheck /> Family-owned and operated since 2005</CheckItem>
            <CheckItem><FiCheck /> Full-service garden center open to the public</CheckItem>
            <CheckItem><FiCheck /> Licensed and insured professionals</CheckItem>
            <CheckItem><FiCheck /> Commercial and residential services</CheckItem>
            <CheckItem><FiCheck /> Year-round service including snow removal</CheckItem>
          </CheckList>

          <CTAButton onClick={() => scrollTo('contact')}>
            Get In Touch
          </CTAButton>
        </Content>
      </Container>
    </AboutSection>
  );
}
